import type { RealtimeGame, RealtimeState } from "../realtime";
import { createRng } from "../rng";

/**
 * Falling blocks, as the second game on the realtime contract.
 *
 * Nibble proved the contract works. This is the one that shows it generalises:
 * a different rate, a much bigger state, a piece that must be *steered* rather
 * than merely turned, and — unlike a snake — an input that ends a piece then
 * and there. If the same `verifyRun` still agrees with the same device loop
 * over all of that, the contract is doing its job rather than fitting one game.
 *
 * Twelve steps a second, so gravity at level one is a comfortable one row a
 * second. That is still three times what the room's alarm floor could drive,
 * which is why it is here rather than in `GAME_LIST`.
 *
 * Integers throughout, for the same reason as Nibble: the browser and the
 * Durable Object have to reach identical states, and a float that rounds
 * differently on two devices is a desync that surfaces as an accusation.
 */

export const TUMBLE_WIDTH = 10;
export const TUMBLE_HEIGHT = 20;
export const TUMBLE_RATE = 12;
/** Twelve a second, so five minutes — a long game for a nine-year-old. */
export const TUMBLE_MAX_STEPS = 3600;
/** Four wide, so a piece box is centred on a ten-wide board. */
export const TUMBLE_SPAWN_X = 3;

/**
 * The seven shapes, as 4×4 bit masks — one per quarter turn.
 *
 * The top-left cell is the high bit, so bit `n` is row `n / 4`, column `n % 4`.
 * Masks rather than co-ordinate lists because a shape is then a single number
 * to compare, copy and reason about, and rotation is a lookup rather than a
 * calculation that could round.
 */
export const TUMBLE_PIECES = [
	[0x0f00, 0x2222, 0x00f0, 0x4444], // line
	[0x8e00, 0x6440, 0x0e20, 0x44c0], // J
	[0x2e00, 0x4460, 0x0e80, 0xc440], // L
	[0x6600, 0x6600, 0x6600, 0x6600], // square
	[0x6c00, 0x4620, 0x06c0, 0x8c40], // S
	[0x4e00, 0x4640, 0x0e40, 0x4c40], // T
	[0xc600, 0x2640, 0x0c60, 0x4c80], // Z
] as const;

/**
 * What clearing rows at once is worth, by how many went together.
 *
 * Steeply more than four singles, because clearing four at once is the thing
 * worth learning, and a scoring table that does not say so teaches nothing.
 */
const TUMBLE_ROW_SCORE = [0, 100, 300, 500, 800] as const;

export interface TumbleState extends RealtimeState {
	kind: "tumble";
	/** Row-major, `y * WIDTH + x`. 0 is empty; otherwise the shape's index + 1. */
	cells: number[];
	piece: number;
	rotation: number;
	x: number;
	y: number;
	/** How many pieces have been dealt, which is what deals the next one. */
	dealt: number;
	lines: number;
	/** Steps since gravity last pulled. */
	sinceFall: number;
}

/**
 * A move, and for the sideways ones how many columns of it.
 *
 * The magnitude is what makes dragging work. One input is applied per step, so
 * without it a finger that crossed nine columns queued nine separate presses
 * and the piece spent most of a second catching up — a thumb this game is
 * played with does not wait. With it, one report of a finger's travel is one
 * input, and the piece arrives where the finger is.
 *
 * It is also the first input in any of these games to carry a number, which is
 * the case `verifyRun`'s parser boundary was put back for: `by` comes off the
 * wire and is bounded here, so no log can ask for a move the board does not
 * have room for.
 */
export type TumbleInput =
	| { move: "left" | "right"; by?: number }
	| { move: "down" | "rotate" | "drop" };

export const tumbleLevel = (lines: number): number => 1 + Math.floor(lines / 10);

/** Steps between one row of gravity and the next. Level one is a row a second. */
export const tumbleFallEvery = (lines: number): number => Math.max(1, 13 - tumbleLevel(lines));

/**
 * The order of one bag of seven.
 *
 * A bag rather than seven independent picks: with independent picks a child can
 * go two minutes without a long bar, decide the game is broken, and be entirely
 * right about it. Every seven pieces is every seven shapes, in an order the
 * seed decides.
 */
function bagFor(seed: number, bag: number): number[] {
	const rng = createRng((seed + bag * 2_654_435_761) >>> 0);
	return rng.shuffle([0, 1, 2, 3, 4, 5, 6]);
}

/** Which shape the nth piece is. Derived, so nothing has to store the queue. */
export function tumblePiece(seed: number, dealt: number): number {
	return bagFor(seed, Math.floor(dealt / 7))[dealt % 7];
}

/** Every board cell a shape covers at this position, top-left first. */
export function tumbleCells(piece: number, rotation: number, x: number, y: number): number[] {
	const mask = TUMBLE_PIECES[piece][rotation];
	const covered: number[] = [];
	for (let bit = 0; bit < 16; bit++) {
		if ((mask & (0x8000 >>> bit)) === 0) continue;
		covered.push((y + (bit >>> 2)) * TUMBLE_WIDTH + (x + (bit & 3)));
	}
	return covered;
}

function collides(
	cells: readonly number[],
	piece: number,
	rotation: number,
	x: number,
	y: number,
): boolean {
	const mask = TUMBLE_PIECES[piece][rotation];
	for (let bit = 0; bit < 16; bit++) {
		if ((mask & (0x8000 >>> bit)) === 0) continue;
		const cx = x + (bit & 3);
		const cy = y + (bit >>> 2);
		if (cx < 0 || cx >= TUMBLE_WIDTH || cy >= TUMBLE_HEIGHT) return true;
		if (cy >= 0 && cells[cy * TUMBLE_WIDTH + cx] !== 0) return true;
	}
	return false;
}

/** Where a shape would come to rest if it fell from here. */
export function tumbleLanding(
	cells: readonly number[],
	piece: number,
	rotation: number,
	x: number,
	y: number,
): number {
	let landed = y;
	while (!collides(cells, piece, rotation, x, landed + 1)) landed++;
	return landed;
}

/** Writes a shape into a copy of the board. */
function settle(
	cells: readonly number[],
	piece: number,
	rotation: number,
	x: number,
	y: number,
): number[] {
	const next = [...cells];
	for (const at of tumbleCells(piece, rotation, x, y)) {
		if (at >= 0 && at < next.length) next[at] = piece + 1;
	}
	return next;
}

/**
 * Lands the piece where it is, clears whatever that filled, and deals the next.
 *
 * The level is read *before* the lines are added, so a clear is worth what it
 * was worth when the child made it rather than what the next level pays.
 */
function land(state: TumbleState): TumbleState {
	const filled = settle(state.cells, state.piece, state.rotation, state.x, state.y);

	const kept: number[] = [];
	let cleared = 0;
	for (let y = 0; y < TUMBLE_HEIGHT; y++) {
		const row = filled.slice(y * TUMBLE_WIDTH, (y + 1) * TUMBLE_WIDTH);
		if (row.every((cell) => cell !== 0)) cleared++;
		else kept.push(...row);
	}
	const cells = [...new Array(cleared * TUMBLE_WIDTH).fill(0), ...kept];

	const dealt = state.dealt + 1;
	const piece = tumblePiece(state.seed, dealt);
	const next: TumbleState = {
		...state,
		cells,
		lines: state.lines + cleared,
		score: state.score + TUMBLE_ROW_SCORE[cleared] * tumbleLevel(state.lines),
		dealt,
		piece,
		rotation: 0,
		x: TUMBLE_SPAWN_X,
		y: 0,
		sinceFall: 0,
	};

	// Nowhere to put the next one is the only way this game ends.
	if (collides(cells, piece, 0, TUMBLE_SPAWN_X, 0)) return { ...next, over: true };
	return next;
}

/** Kicks tried in order when a turn would not otherwise fit. */
const TUMBLE_KICKS = [0, -1, 1, -2, 2] as const;

export const tumble: RealtimeGame<TumbleState, TumbleInput> = {
	id: "tumble",
	rate: TUMBLE_RATE,
	maxSteps: TUMBLE_MAX_STEPS,
	/*
	 * A boardful, because this one is played by dragging.
	 *
	 * One drag across the board is ten presses made in a tenth of a second, and
	 * the default queue of three simply lost seven of them — a finger that
	 * crossed nine columns moved the piece two, which reads as the board
	 * ignoring you. Ten is the most a drag can ever mean, since that is how wide
	 * the board is, and they still leave one per step so the piece slides after
	 * the finger rather than teleporting.
	 */
	maxQueued: TUMBLE_WIDTH,

	create(seed) {
		return {
			kind: "tumble",
			step: 0,
			seed,
			over: false,
			score: 0,
			cells: new Array(TUMBLE_WIDTH * TUMBLE_HEIGHT).fill(0),
			piece: tumblePiece(seed, 0),
			rotation: 0,
			x: TUMBLE_SPAWN_X,
			y: 0,
			dealt: 0,
			lines: 0,
			sinceFall: 0,
		};
	},

	parseInput(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { move, by } = raw as { move?: unknown; by?: unknown };

		if (move === "down" || move === "rotate" || move === "drop") return { move };
		if (move !== "left" && move !== "right") return null;

		if (by === undefined) return { move };
		// A whole number of columns, and never more than the board is wide: a
		// bigger one is not a drag, it is a number somebody typed.
		if (typeof by !== "number" || !Number.isInteger(by)) return null;
		if (by < 1 || by > TUMBLE_WIDTH) return null;
		return { move, by };
	},

	step(state, input) {
		if (state.over) return state;

		const next: TumbleState = { ...state, step: state.step + 1 };

		switch (input?.move) {
			case "left":
			case "right": {
				// One column at a time, stopping at whatever is in the way — so a
				// drag across a stack slides up to it rather than through it.
				const step = input.move === "left" ? -1 : 1;
				for (let n = 0; n < (input.by ?? 1); n++) {
					if (collides(next.cells, next.piece, next.rotation, next.x + step, next.y)) break;
					next.x += step;
				}
				break;
			}
			case "rotate": {
				const rotation = (next.rotation + 1) % 4;
				// Against a wall or a stack, a turn that would not fit shuffles
				// sideways to make room. Without this a piece resting on the left
				// edge simply cannot be turned, which reads as the button being
				// broken rather than as a rule.
				for (const kick of TUMBLE_KICKS) {
					if (!collides(next.cells, next.piece, rotation, next.x + kick, next.y)) {
						next.rotation = rotation;
						next.x += kick;
						break;
					}
				}
				break;
			}
			case "down":
				if (!collides(next.cells, next.piece, next.rotation, next.x, next.y + 1)) {
					next.y += 1;
					// Nudging it down buys a full interval before gravity pulls
					// again, so holding down is faster than gravity rather than the
					// two of them fighting over the same rows.
					next.sinceFall = 0;
				}
				break;
			case "drop":
				next.y = tumbleLanding(next.cells, next.piece, next.rotation, next.x, next.y);
				return land(next);
		}

		next.sinceFall += 1;
		if (next.sinceFall >= tumbleFallEvery(next.lines)) {
			next.sinceFall = 0;
			if (collides(next.cells, next.piece, next.rotation, next.x, next.y + 1)) return land(next);
			next.y += 1;
		}

		return next;
	},
};

// --- The computer opponent -------------------------------------------------

/*
 * Weights from the well-known Dellacherie-style evaluation, multiplied by a
 * hundred so the whole thing stays in integers. Only the server ever runs this
 * — the bot's *output* is inputs, which are replayed like anyone else's — but
 * keeping it integral means a test that pins a bot run stays pinned.
 */
const HEIGHT_WEIGHT = -510;
const LINES_WEIGHT = 760;
const HOLES_WEIGHT = -360;
const BUMPINESS_WEIGHT = -180;

/** How good a settled board is. Higher is better, and only relatively. */
function judge(cells: readonly number[]): number {
	const heights: number[] = [];
	for (let x = 0; x < TUMBLE_WIDTH; x++) {
		let height = 0;
		for (let y = 0; y < TUMBLE_HEIGHT; y++) {
			if (cells[y * TUMBLE_WIDTH + x] !== 0) {
				height = TUMBLE_HEIGHT - y;
				break;
			}
		}
		heights.push(height);
	}

	let holes = 0;
	for (let x = 0; x < TUMBLE_WIDTH; x++) {
		let covered = false;
		for (let y = 0; y < TUMBLE_HEIGHT; y++) {
			if (cells[y * TUMBLE_WIDTH + x] !== 0) covered = true;
			else if (covered) holes++;
		}
	}

	let bumpiness = 0;
	for (let x = 0; x < TUMBLE_WIDTH - 1; x++) bumpiness += Math.abs(heights[x] - heights[x + 1]);

	let complete = 0;
	for (let y = 0; y < TUMBLE_HEIGHT; y++) {
		let full = true;
		for (let x = 0; x < TUMBLE_WIDTH; x++) {
			if (cells[y * TUMBLE_WIDTH + x] === 0) {
				full = false;
				break;
			}
		}
		if (full) complete++;
	}

	const aggregate = heights.reduce((total, height) => total + height, 0);
	return (
		HEIGHT_WEIGHT * aggregate +
		LINES_WEIGHT * complete +
		HOLES_WEIGHT * holes +
		BUMPINESS_WEIGHT * bumpiness
	);
}

/**
 * Where this piece would be best put, of everywhere it could go.
 *
 * From `-3`, because a shape's mask can have empty columns down its left side —
 * the upright line sits in column two of its own box, so it only reaches the
 * wall at `x = -2`.
 */
function bestPlacement(state: TumbleState): { rotation: number; x: number } | null {
	let best: { rotation: number; x: number; value: number } | null = null;

	for (let rotation = 0; rotation < 4; rotation++) {
		for (let x = -3; x < TUMBLE_WIDTH; x++) {
			if (collides(state.cells, state.piece, rotation, x, 0)) continue;
			const y = tumbleLanding(state.cells, state.piece, rotation, x, 0);
			const value = judge(settle(state.cells, state.piece, rotation, x, y));
			if (!best || value > best.value) best = { rotation, x, value };
		}
	}
	return best;
}

/*
 * Robo's fallibility, in the same spirit as Flit's `FLYER_REACTION` and Zoom's
 * `ROBO_REACTION`: the clean Dellacherie search plays essentially perfect
 * falling blocks — it survived the whole five-minute run on nearly nine seeds
 * in ten and banked a mean of half a million points, against the few thousand
 * a strong child manages. A racemate has to be beatable, so the *plan* is
 * corrupted per piece: sometimes the target column is a step off, and
 * sometimes Robo simply does not think and lets the piece fall where it
 * spawned. Off-by-one placements dig the holes that no later piece can fill,
 * which is exactly how a human run decays.
 *
 * Tuned empirically over dozens of seeds: at these rates Robo scores a few
 * thousand with a spread of thousands, and dies mid-run on most seeds rather
 * than outliving the clock.
 */
export const ROBO_WOBBLE = 0.12;
export const ROBO_LAPSE = 0.02;

/**
 * Robo playing falling blocks: work out where the piece belongs, then take one
 * step towards putting it there.
 *
 * A factory rather than a plain function, because the search is the expensive
 * part of this file — four rotations by thirteen columns, each landed and
 * judged over two hundred cells — and running it on every step would repeat it
 * a dozen times per piece for an answer that cannot have changed. Fifteen test
 * runs took ten seconds that way, and the same cost would land inside a Durable
 * Object, which is a worse place to spend it.
 *
 * The plan is keyed on how many pieces have been dealt, which is exactly when
 * the board can have changed — so this is a cache with no staleness in it
 * rather than an approximation.
 *
 * The mistakes are dealt from the game's own seed and piece count, never from
 * `Math.random`: a Robo run is verified by replaying its inputs like anyone
 * else's, so the same seed must always produce the same run.
 *
 * It cannot get stuck asking for a turn it will never get, because gravity does
 * not consult the player: a piece it is fruitlessly trying to rotate still
 * falls, still lands, and is still replaced.
 */
export function tumbleRobo(): (state: TumbleState) => TumbleInput | null {
	let plannedFor = -1;
	let target: { rotation: number; x: number } | null = null;

	return (state) => {
		if (state.dealt !== plannedFor) {
			plannedFor = state.dealt;
			// A different multiplier from the bag's, so the mistakes and the
			// piece order come off unrelated streams of the same seed.
			const slip = createRng((state.seed + 1 + state.dealt * 15_485_863) >>> 0);
			if (slip.next() < ROBO_LAPSE) {
				// A lapse: no plan at all, the piece drops where it appeared.
				target = null;
			} else {
				target = bestPlacement(state);
				if (target && slip.next() < ROBO_WOBBLE) {
					// A wobble: the right idea, one column off. Kept on the board
					// so the piece still lands somewhere near the mistake rather
					// than grinding against a wall for the rest of its fall.
					const x = target.x + (slip.next() < 0.5 ? -1 : 1);
					if (!collides(state.cells, state.piece, target.rotation, x, 0)) {
						target = { rotation: target.rotation, x };
					}
				}
			}
		}
		if (!target) return { move: "drop" };
		if (state.rotation !== target.rotation) return { move: "rotate" };
		if (state.x > target.x) return { move: "left" };
		if (state.x < target.x) return { move: "right" };
		return { move: "drop" };
	};
}

/**
 * A policy that only ever drops where the piece appeared.
 *
 * Deliberately poor, and useful for exactly that: it fills the board quickly
 * and reliably, so a test that needs a run to *end* does not have to wait out
 * the five-minute ceiling to get one.
 */
export function tumbleStackUp(): TumbleInput {
	return { move: "drop" };
}
